import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { invitePerson } from '../../api/user-invitations'
import type { UserRole } from '../../types/api'

export function InvitePersonModal({
  isOpen,
  onClose,
  onSuccess,
  orgId,
}: {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => Promise<void>
  orgId?: number
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!isOpen) return
    setError('')
    const previous = document.activeElement as HTMLElement | null
    box.current?.querySelector<HTMLInputElement>('input')?.focus()
    return () => previous?.focus()
  }, [isOpen])
  if (!isOpen) return null
  const input =
    'mt-1 w-full rounded-lg border border-harbor-border bg-harbor-bg px-3 py-3 text-harbor-text focus-visible:ring-2 focus-visible:ring-emerald-500'
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-title"
        className="w-full max-w-lg max-h-[90vh] overflow-auto rounded-2xl border border-harbor-border bg-harbor-surface p-6 shadow-2xl"
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !busy) onClose()
          if (e.key === 'Tab') {
            const elements = box.current?.querySelectorAll<HTMLElement>(
              'input, select, button:not(:disabled)'
            )
            if (!elements?.length) return
            const first = elements[0],
              last = elements[elements.length - 1]
            if (e.shiftKey && document.activeElement === first) {
              e.preventDefault()
              last.focus()
            } else if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault()
              first.focus()
            }
          }
        }}
      >
        <h2 id="invite-title" className="text-xl font-semibold text-harbor-text">
          Invite person
        </h2>
        <p className="mt-2 text-sm text-harbor-text-secondary">
          Give someone a place in your workspace. Their invitation is valid for seven days.
        </p>
        <form
          className="mt-6 space-y-4"
          onSubmit={async (e) => {
            e.preventDefault()
            const form = new FormData(e.currentTarget)
            setBusy(true)
            setError('')
            try {
              const password = String(form.get('password') ?? '')
              const result = await invitePerson({
                email: String(form.get('email')).trim(),
                username: String(form.get('username')).trim(),
                role: form.get('role') as UserRole,
                ...(password ? { password } : {}),
                orgId,
              })
              if (result.emailSent) toast.success('Invitation sent')
              else
                toast.error(
                  'Invitation saved, but email delivery failed. Use Copy invite link or Resend invite.'
                )
              await onSuccess()
              onClose()
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Unable to invite person')
            } finally {
              setBusy(false)
            }
          }}
        >
          {error && (
            <p role="alert" className="rounded-lg bg-red-500/10 p-3 text-red-500">
              {error}
            </p>
          )}
          <label className="block text-sm text-harbor-text">
            Email
            <input className={input} name="email" type="email" required autoComplete="off" />
          </label>
          <label className="block text-sm text-harbor-text">
            Username
            <input className={input} name="username" required autoComplete="off" />
          </label>
          <label className="block text-sm text-harbor-text">
            Role
            <select className={input} name="role" defaultValue="user">
              <option value="user">User</option>
              <option value="project_manager">Project Manager</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <label className="block text-sm text-harbor-text">
            Password (optional)
            <input
              className={input}
              name="password"
              type="password"
              minLength={8}
              autoComplete="new-password"
              aria-describedby="invite-password-help"
            />
          </label>
          <p id="invite-password-help" className="text-sm text-harbor-text-secondary">
            Leave blank to let them sign in with Google, Microsoft, or GitHub using their verified
            email. They can set a password from Profile later.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              disabled={busy}
              onClick={onClose}
              className="min-h-11 rounded-lg border border-harbor-border px-4 text-harbor-text"
            >
              Cancel
            </button>
            <button
              disabled={busy}
              className="min-h-11 rounded-lg bg-emerald-600 px-5 font-medium text-white disabled:opacity-50"
            >
              {busy ? 'Sending invitation…' : 'Send invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
